/**
 * CheTuo Web — SaaS admin API (dashboard, shops, subscriptions, etc.)
 */

import { apiClient } from './client';

// ── Dashboard ─────────────────────────────────────────────────

export interface AdminDashboard {
  shops: {
    total: number;
    trial: number;
    active: number;
    readonly: number;
    expired: number;
  };
  subscriptions: {
    expiringIn30Days: number;
  };
  orders: {
    pending: number;
    paid: number;
  };
}

export function getAdminDashboard() {
  return apiClient.get<AdminDashboard>('/api/admin/dashboard');
}

// ── Shops ─────────────────────────────────────────────────────

export interface AdminShop {
  shop: {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    trialStartedAt: string;
    activatedAt: string | null;
  };
  owner: { id: string; phone: string; displayName: string | null } | null;
  subscription: { id: string; status: string; endAt: string } | null;
  plan: { id: string; code: string; name: string } | null;
  memberCount: number;
}

export interface AdminShopsResult {
  rows: AdminShop[];
  total: number;
  page: number;
  pageSize: number;
}

export function listAdminShops(params?: {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<AdminShopsResult> {
  const query: string[] = [];
  if (params?.status) query.push(`status=${encodeURIComponent(params.status)}`);
  if (params?.search) query.push(`search=${encodeURIComponent(params.search)}`);
  if (params?.page) query.push(`page=${params.page}`);
  if (params?.pageSize) query.push(`pageSize=${params.pageSize}`);
  const qs = query.length > 0 ? `?${query.join('&')}` : '';
  return apiClient.get<AdminShopsResult>(`/api/admin/shops${qs}`);
}

// ── Subscriptions ─────────────────────────────────────────────

export interface AdminSubscription {
  subscription: {
    id: string;
    shopId: string;
    planId: string;
    status: string;
    startAt: string;
    endAt: string;
    manualExtensionDays: number;
  };
  shop: { id: string; name: string } | null;
  plan: { id: string; code: string; name: string } | null;
}

export interface AdminSubscriptionsResult {
  rows: AdminSubscription[];
  total: number;
  page: number;
  pageSize: number;
}

export function listAdminSubscriptions(params?: {
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<AdminSubscriptionsResult> {
  const query: string[] = [];
  if (params?.status) query.push(`status=${encodeURIComponent(params.status)}`);
  if (params?.page) query.push(`page=${params.page}`);
  if (params?.pageSize) query.push(`pageSize=${params.pageSize}`);
  const qs = query.length > 0 ? `?${query.join('&')}` : '';
  return apiClient.get<AdminSubscriptionsResult>(`/api/admin/subscriptions${qs}`);
}

// ── Audit Logs ────────────────────────────────────────────────

export interface AuditLog {
  log: {
    id: string;
    shopId: string | null;
    actorUserId: string;
    action: string;
    targetType: string;
    targetId: string;
    detail: Record<string, unknown>;
    createdAt: string;
  };
  actor: { id: string; phone: string; displayName: string | null } | null;
  shop: { id: string; name: string } | null;
}

export interface AuditLogsResult {
  rows: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
}

export function listAuditLogs(params?: {
  shopId?: string;
  action?: string;
  page?: number;
  pageSize?: number;
}): Promise<AuditLogsResult> {
  const query: string[] = [];
  if (params?.shopId) query.push(`shopId=${encodeURIComponent(params.shopId)}`);
  if (params?.action) query.push(`action=${encodeURIComponent(params.action)}`);
  if (params?.page) query.push(`page=${params.page}`);
  if (params?.pageSize) query.push(`pageSize=${params.pageSize}`);
  const qs = query.length > 0 ? `?${query.join('&')}` : '';
  return apiClient.get<AuditLogsResult>(`/api/admin/audit-logs${qs}`);
}

// ── Admin Users ───────────────────────────────────────────────

export interface AdminUser {
  id: string;
  phone: string;
  displayName: string | null;
  isSuperAdmin: boolean;
  createdAt: string;
}

export interface AdminUsersResult {
  rows: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
}

export function listAdminUsers(params?: {
  page?: number;
  pageSize?: number;
}): Promise<AdminUsersResult> {
  const query: string[] = [];
  if (params?.page) query.push(`page=${params.page}`);
  if (params?.pageSize) query.push(`pageSize=${params.pageSize}`);
  const qs = query.length > 0 ? `?${query.join('&')}` : '';
  return apiClient.get<AdminUsersResult>(`/api/admin/admin-users${qs}`);
}

// ── Plan Catalogs ─────────────────────────────────────────────

export interface PlanCatalog {
  id: string;
  code: string;
  name: string;
  annualPriceCents: number;
  trialDays: number;
  features: string;
  suitableFor: string;
  multiShop: boolean;
  enabled: boolean;
  sortOrder: number;
}

export function listPlanCatalogs(): Promise<PlanCatalog[]> {
  return apiClient.get<PlanCatalog[]>('/api/admin/plans');
}

// ── Operations ────────────────────────────────────────────────

export function manualRenewShop(
  shopId: string,
  input: { days?: number; amountCents?: number; channel?: string; remark?: string },
) {
  return apiClient.post<{
    order: { id: string; status: string; amountCents: number };
    subscription: { id: string; endAt: string; status: string };
  }>(`/api/admin/shops/${shopId}/manual-renew`, input);
}

export function updateShopStatus(shopId: string, status: 'trial' | 'active' | 'readonly' | 'expired') {
  return apiClient.patch(`/api/admin/shops/${shopId}/status`, { status });
}

export function createAdminUser(input: {
  username: string;
  password: string;
  displayName?: string;
}) {
  return apiClient.post<{ id: string; phone: string; displayName: string | null }>(
    '/api/admin/admin-users',
    input,
  );
}
